import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, User, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { submitContactForm } from "@/lib/api";
import { toast } from "sonner";

const CONTACT_EMAIL = "hello.digitalproperties@gmail.com";

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContactForm(form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="contact-page">
      <SeoHead
        title="Contact Us"
        description="Get in touch with PublicBeachAccess.com. Questions about beach access, data corrections, partnerships, or general inquiries — we'd love to hear from you."
        path="/contact"
      />

      <div className="bg-[#0EA5E9] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-['Outfit']">Contact Us</h1>
          <p className="text-white/80 mt-2">We'd love to hear from you</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-md p-6">
              <h2 className="text-lg font-bold text-gray-900 font-['Outfit'] mb-4">Get In Touch</h2>
              <p className="text-gray-600 text-sm mb-6">
                Have a question about beach access, need a data correction, or want to partner with us? Send us a message and we'll get back to you as soon as possible.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Email</p>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-sky-600 hover:text-sky-700" data-testid="contact-email-link">
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="text-sm font-bold text-gray-800 mb-2">Common Inquiries</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>Data corrections or updates</li>
                <li>Adding a new beach access point</li>
                <li>Partnership opportunities</li>
                <li>Advertising inquiries</li>
                <li>General feedback</li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-3">
            {submitted ? (
              <div className="bg-white rounded-2xl shadow-md p-8 text-center" data-testid="contact-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 font-['Outfit'] mb-2">Message Sent!</h2>
                <p className="text-gray-600 mb-6">Thank you for reaching out. We'll get back to you at the email you provided.</p>
                <Button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", subject: "", message: "" }); }} className="bg-[#0EA5E9] hover:bg-[#0284C7]">
                  Send Another Message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-md p-6 sm:p-8 space-y-5" data-testid="contact-form">
                <h2 className="text-lg font-bold text-gray-900 font-['Outfit'] mb-1">Send a Message</h2>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">Your Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="name"
                      data-testid="contact-name"
                      placeholder="John Doe"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1.5">Subject</label>
                  <Input
                    id="subject"
                    data-testid="contact-subject"
                    placeholder="What's this about?"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    placeholder="Tell us how we can help..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={5}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold h-11"
                >
                  {submitting ? "Sending..." : "Send Message"}
                  {!submitting && <Send className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
